import React, { useRef, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Block } from './Block';
import { BlockWithRevisions } from '@/types';
import { calculateBlockLayout } from '@/utils/overlapLayout';
import styles from './Timeline.module.css';

interface TimelineProps {
  blocks: BlockWithRevisions[];
  startHour?: number;
  endHour?: number;
  onBlockClick?: (block: BlockWithRevisions) => void;
  onEmptySlotClick?: (startTime: number) => void;
}

const HOUR_HEIGHT = 80; // px per hour

export function Timeline({
  blocks,
  startHour = 6,
  endHour = 22,
  onBlockClick,
  onEmptySlotClick,
}: TimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<HTMLDivElement>(null);

  // Live current time - updates every minute
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Update immediately to sync with actual time
    setNow(new Date());

    // Update every minute
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Generate hour labels
  const hours = useMemo(() => {
    const result = [];
    for (let h = startHour; h <= endHour; h++) {
      result.push(h);
    }
    return result;
  }, [startHour, endHour]);

  // Calculate current time position
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const timelineStartMinutes = startHour * 60;
  const timelineEndMinutes = endHour * 60;
  const showCurrentTime =
    currentMinutes >= timelineStartMinutes && currentMinutes <= timelineEndMinutes;
  const currentTimePosition = showCurrentTime
    ? ((currentMinutes - timelineStartMinutes) / 60) * HOUR_HEIGHT
    : 0;

  // Scroll to current time on mount
  useEffect(() => {
    if (currentTimeRef.current && containerRef.current) {
      const container = containerRef.current;
      const currentTimeEl = currentTimeRef.current;
      const containerHeight = container.clientHeight;
      const scrollTarget = currentTimeEl.offsetTop - containerHeight / 3;
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
    }
  }, []);

  // Handle clicking on empty slot
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onEmptySlotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (containerRef.current?.scrollTop || 0);
    const minutes = timelineStartMinutes + (y / HOUR_HEIGHT) * 60;
    // Round to nearest 15 minutes
    const roundedMinutes = Math.round(minutes / 15) * 15;
    onEmptySlotClick(roundedMinutes);
  };

  // Format hour label
  const formatHour = (hour: number) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    return format(date, 'h:mm a');
  };

  // Format current time
  const formatCurrentTime = () => {
    return format(now, 'h:mm a');
  };

  // Calculate block layout for overlapping blocks
  const { layoutMap: blockLayout } = useMemo(() => calculateBlockLayout(blocks), [blocks]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={styles.timeline}
        onClick={handleTimelineClick}
        style={{ height: hours.length * HOUR_HEIGHT }}
      >
        {/* Hour grid lines */}
        {hours.map((hour) => (
          <div
            key={hour}
            className={styles.hourRow}
            style={{ top: (hour - startHour) * HOUR_HEIGHT }}
          >
            <span className={styles.hourLabel}>{formatHour(hour)}</span>
          </div>
        ))}

        {/* Current time indicator */}
        {showCurrentTime && (
          <motion.div
            ref={currentTimeRef}
            className={styles.currentTime}
            style={{ top: currentTimePosition }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className={styles.currentTimeLabel}>{formatCurrentTime()}</span>
            <div className={styles.currentTimeDot} />
            <div className={styles.currentTimeLine} />
          </motion.div>
        )}

        {/* Blocks */}
        {blocks.map((block) => {
          const layout = blockLayout.get(block.id) || { columnIndex: 0, totalColumns: 1 };
          return (
            <Block
              key={block.id}
              block={block}
              startHour={startHour}
              hourHeight={HOUR_HEIGHT}
              onClick={() => onBlockClick?.(block)}
              columnIndex={layout.columnIndex}
              totalColumns={layout.totalColumns}
            />
          );
        })}
      </div>
    </div>
  );
}

export default Timeline;
